// import { useMemo } from 'react';
import { OvertimeRequest, User } from "@/types"
import { useAuth } from '@/contexts/auth';
import { UserRole } from '@/types/enums';
// import { useEmployees } from '@/contexts/employees/EmployeesContext';
import { ResidencyType } from '@/contexts/employees/EmployeesContext';
import { getMonthAbbreviation } from '@/constants/months';
import { normalizeResidency, RESIDENCY_VALUES } from '@/utils/residencyUtils';

/**
 * Hook for overtime analytics functionality
 */
export const useAnalyticsData = (
  overtimeRequests: OvertimeRequest[],
  employees: User[]
) => {
  const { user } = useAuth();
  
  const getOvertimeByDepartment = () => {
    try {
      const departmentData: { [key: string]: number } = {}
      let totalHours = 0;
      
      // Filter by company if superadmin
      const filteredRequests = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? overtimeRequests.filter(req => {
            // For superadmins, we want to filter by their current company
            const employee = employees.find(emp => emp.id === req.employeeId);
            return employee && employee.companyId === user.companyId;
          })
        : overtimeRequests;
      
      filteredRequests.forEach(request => {
        if (request.department) {
          if (departmentData[request.department]) {
            departmentData[request.department] += request.hours
          } else {
            departmentData[request.department] = request.hours
          }
          totalHours += request.hours;
        }
      })
      
      return Object.entries(departmentData).map(([department, hours]) => ({
        department,
        hours,
        percentage: totalHours > 0 ? (hours / totalHours) * 100 : 0
      }))
    } catch {
      return [];
    }
  }
  
  const getOvertimeTrend = () => {
    try {
      // Filter requests based on user context
      const filteredRequests = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? overtimeRequests.filter(req => {
            const employee = employees.find(emp => emp.id === req.employeeId);
            return employee && employee.companyId === user.companyId;
          })
        : overtimeRequests;
      
      if (!filteredRequests || filteredRequests.length === 0) {
        return [];
      }
      
      const monthlyData: { [key: string]: number } = {}
      
      // Process each request and aggregate by month
      filteredRequests.forEach(request => {
        if (request.date) {
          try {
            // Extract month from date (works with both string and Date objects)
            let month: string;
            
            if (typeof request.date === 'string') {
              // If it contains a dash, it's likely a full date format (YYYY-MM-DD)
              if (request.date.includes('-')) {
                const dateObj = new Date(request.date);
                if (!isNaN(dateObj.getTime())) {
                  month = getMonthAbbreviation(dateObj.getMonth());
                } else {
                  // Fallback for invalid dates
                  month = request.date.substring(0, 3); // Take first 3 chars as month abbr
                }
              } else {
                // It's already a month abbreviation or something else
                month = request.date;
              }
            } else if (request.date && typeof request.date === 'object' && 'getMonth' in request.date) {
              // Safely check if it's a Date object by looking for Date methods
              const dateObj = request.date as Date; // Explicit type cast for TypeScript
              month = getMonthAbbreviation(dateObj.getMonth());
            } else {
              // Fallback for unknown type
              month = 'Unknown';
            }
            
            if (monthlyData[month]) {
              monthlyData[month] += request.hours;
            } else {
              monthlyData[month] = request.hours;
            }
          } catch {
            // removed empty block
          }
        }
      });
      
      // Convert the aggregated data to the expected format
      const result = Object.entries(monthlyData).map(([month, hours]) => ({
        name: month,
        value: hours,
        date: month,
        hours: hours
      }));
      
      return result;
    } catch {
      return [];
    }
  }

  const getProductivityMetrics = () => {
    try {
      return [
        { name: 'Efficiency', value: 85 },
        { name: 'Accuracy', value: 92 },
        { name: 'On-Time Delivery', value: 98 },
      ];
    } catch {
      return [];
    }
  }
  
  const getDepartmentComparison = () => {
    try {
      // Filter employees based on user context
      const filteredEmployees = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? employees.filter(emp => emp.companyId === user.companyId)
        : employees;
      
      // Filter requests based on user context
      const filteredRequests = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? overtimeRequests.filter(req => {
            const employee = employees.find(emp => emp.id === req.employeeId);
            return employee && employee.companyId === user.companyId;
          })
        : overtimeRequests;
      
      const deptData: { [key: string]: { hours: number, employees: number } } = {}
      
      filteredEmployees.forEach(emp => {
        if (!emp.department) return
        
        if (!deptData[emp.department]) {
          deptData[emp.department] = { hours: 0, employees: 0 }
        }
        
        deptData[emp.department].employees += 1
      })
      
      filteredRequests.forEach(req => {
        if (!req.department) return
        
        if (!deptData[req.department]) {
          deptData[req.department] = { hours: 0, employees: 0 }
        }
        
        deptData[req.department].hours += req.hours
      })
      
      return Object.entries(deptData).map(([department, data]) => ({
        department,
        hours: data.hours,
        perEmployee: data.employees > 0 ? data.hours / data.employees : 0
      }))
    } catch {
      return [];
    }
  }
  
  const getShiftOvertimeTrends = () => {
    try {
      // Filter employees and requests based on user context
      const filteredEmployees = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? employees.filter(emp => emp.companyId === user.companyId)
        : employees;
        
      const filteredRequests = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? overtimeRequests.filter(req => {
            const employee = employees.find(emp => emp.id === req.employeeId);
            return employee && employee.companyId === user.companyId;
          })
        : overtimeRequests;

      // Create a map for months to ensure consistent data format
      const months = Array.from({ length: 12 }, (_, i) => getMonthAbbreviation(i));
      const shiftMonthData: Record<string, { morningShift: number, afternoonShift: number, nightShift: number }> = {};
      
      // Initialize data structure with all months
      months.forEach(month => {
        shiftMonthData[month] = { 
          morningShift: 0, 
          afternoonShift: 0, 
          nightShift: 0 
        };
      });
      
      // Process request data
      filteredRequests.forEach(req => {
        if (!req.date) return;
        
        const employee = filteredEmployees.find(emp => emp.id === req.employeeId);
        if (!employee || !employee.shift) return;
        
        // Extract month from date
        let month: string;
        try {
          if (typeof req.date === 'string') {
            if (req.date.includes('-')) {
              // It's a full date string
              const dateObj = new Date(req.date);
              if (!isNaN(dateObj.getTime())) {
                month = getMonthAbbreviation(dateObj.getMonth());
              } else {
                month = req.date.substring(0, 3);
              }
            } else {
              // It's already a month abbreviation
              month = req.date;
            }
          } else if (req.date && typeof req.date === 'object' && 'getMonth' in req.date) {
            // Safely check if it's a Date object by looking for Date methods
            const dateObj = req.date as Date; // Explicit type cast for TypeScript
                month = getMonthAbbreviation(dateObj.getMonth());
          } else {
            month = 'Unknown';
          }
        } catch {
          month = 'Unknown';
        }
        
        // Skip if month is invalid
        if (!months.includes(month)) {
          return;
        }
        
        // Map shift times to shift categories
        if (employee.shift.includes('Morning') || employee.shift.includes('AM')) {
          shiftMonthData[month].morningShift += req.hours;
        } else if (employee.shift.includes('Evening') || employee.shift.includes('Afternoon') || employee.shift.includes('PM')) {
          shiftMonthData[month].afternoonShift += req.hours;
        } else if (employee.shift.includes('Night') || employee.shift.includes('Overnight')) {
          shiftMonthData[month].nightShift += req.hours;
        } else {
          // Default to morning if can't categorize
          shiftMonthData[month].morningShift += req.hours;
        }
      });
      
      // Convert to array format needed for chart
      const result = Object.entries(shiftMonthData)
        .filter(([, data]) => data.morningShift > 0 || data.afternoonShift > 0 || data.nightShift > 0)
        .map(([month, data]) => ({
          month,
          morningShift: data.morningShift,
          afternoonShift: data.afternoonShift,
          nightShift: data.nightShift
        }));
        
      return result;
    } catch {
      return [];
    }
  }
  
  const getResidencyData = () => {
    try {
      // Filter employees and requests based on user context
      const filteredEmployees = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? employees.filter(emp => emp.companyId === user.companyId)
        : employees;
        
      const filteredRequests = user?.role === UserRole.SUPER_ADMIN && user?.companyId
        ? overtimeRequests.filter(req => {
            const employee = employees.find(emp => emp.id === req.employeeId);
            return employee && employee.companyId === user.companyId;
          })
        : overtimeRequests;

      let residentHours = 0;
      let nonResidentHours = 0;
      let totalHours = 0;
      
      filteredRequests.forEach(req => {
        const employee = filteredEmployees.find(emp => emp.id === req.employeeId);
        if (!employee) return;
        
        // Use normalized residency comparison
        if (normalizeResidency(employee.residency) === RESIDENCY_VALUES.RESIDENCE) {
          residentHours += req.hours;
        } else {
          nonResidentHours += req.hours;
        }
        
        totalHours += req.hours;
      });
      
      return [
        { 
          category: ResidencyType.RESIDENT, 
          value: residentHours, 
          percentage: totalHours > 0 ? (residentHours / totalHours) * 100 : 0, 
          color: '#4CAF50' 
        },
        { 
          category: ResidencyType.NON_RESIDENT, 
          value: nonResidentHours, 
          percentage: totalHours > 0 ? (nonResidentHours / totalHours) * 100 : 0, 
          color: '#2196F3' 
        },
      ];
    } catch {
      return [
        { category: ResidencyType.RESIDENT, value: 0, percentage: 50, color: '#4CAF50' },
        { category: ResidencyType.NON_RESIDENT, value: 0, percentage: 50, color: '#2196F3' }
      ];
    }
  }
  
  return {
    getOvertimeByDepartment,
    getOvertimeTrend,
    getProductivityMetrics,
    getDepartmentComparison,
    getShiftOvertimeTrends,
    getResidencyData,
  }
}
